<template>
  <div class="app-download-buttons">
    <!-- Заголовок -->
    <div class="text-center mb-8">
      <h1 class="text-3xl md:text-4xl font-bold mb-3">
        {{ title }}
      </h1>
      <p class="text-lg text-gray-600 dark:text-gray-400">
        {{ subtitle }}
      </p>
    </div>

    <!-- Информация об устройстве (для отладки в dev режиме) -->
    <div v-if="showDeviceInfo && deviceType" class="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
      <p class="text-sm text-blue-600 dark:text-blue-400">
        <strong>Определено устройство:</strong> {{ deviceType }}
      </p>
    </div>

    <!-- Кнопки скачивания -->
    <div class="buttons-container">
      <!-- Кнопка Android / RuStore -->
      <!-- Ссылка, а не кнопка: страница лежит в sitemap и собирает запрос
           «скачать приложение <заведение>», а в её разметке не было ни одного
           адреса магазина — только onclick, который поисковик не исполняет.
           Переход по-прежнему открывает новую вкладку, теперь силами браузера. -->
      <a
        v-if="androidUrl"
        :href="androidUrl"
        target="_blank"
        rel="noopener"
        class="download-button android-button"
        @click="handleAndroidClick"
      >
        <div class="button-content">
          <div class="button-icon">
            <!-- Иконка RuStore/Android -->
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              class="w-8 h-8"
            >
              <path d="M17.523 15.3414C18.197 15.3414 18.7426 14.7957 18.7426 14.1218C18.7426 13.4479 18.197 12.9023 17.523 12.9023C16.8491 12.9023 16.3035 13.4479 16.3035 14.1218C16.3035 14.7957 16.8491 15.3414 17.523 15.3414Z"/>
              <path d="M6.47703 15.3414C7.15096 15.3414 7.69656 14.7957 7.69656 14.1218C7.69656 13.4479 7.15096 12.9023 6.47703 12.9023C5.8031 12.9023 5.2575 13.4479 5.2575 14.1218C5.2575 14.7957 5.8031 15.3414 6.47703 15.3414Z"/>
              <path d="M7.37688 8.51172L8.91063 5.90547C9.00906 5.73984 8.95375 5.52422 8.78813 5.42578C8.6225 5.32734 8.40688 5.38266 8.30844 5.54828L6.75094 8.18984C5.52375 7.65078 4.14469 7.35234 2.68313 7.35234C1.22156 7.35234 -0.1575 7.65078 -1.38469 8.18984L-2.94219 5.54828C-3.04063 5.38266 -3.25625 5.32734 -3.42188 5.42578C-3.5875 5.52422 -3.64281 5.73984 -3.54437 5.90547L-2.01062 8.51172C-4.74469 9.95016 -6.66469 12.7195 -6.92156 15.9492H11.9216C11.6647 12.7195 9.74469 9.95016 7.37688 8.51172Z"/>
            </svg>
          </div>
          <div class="button-text">
            <span class="button-label">Скачать на</span>
            <span class="button-store">Android (RuStore)</span>
          </div>
        </div>
      </a>

      <!-- Кнопка iOS / App Store -->
      <a
        v-if="iosUrl"
        :href="iosUrl"
        target="_blank"
        rel="noopener"
        class="download-button ios-button"
        @click="handleIosClick"
      >
        <div class="button-content">
          <div class="button-icon">
            <!-- Иконка Apple -->
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              class="w-8 h-8"
            >
              <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
            </svg>
          </div>
          <div class="button-text">
            <span class="button-label">Скачать на</span>
            <span class="button-store">iOS (App Store)</span>
          </div>
        </div>
      </a>
    </div>

    <!-- Сообщение если нет ссылок -->
    <div v-if="!androidUrl && !iosUrl" class="text-center mt-8">
      <p class="text-gray-600 dark:text-gray-400">
        К сожалению, ссылки на скачивание временно недоступны.
        <br>
        Попробуйте позже или свяжитесь с поддержкой.
      </p>
    </div>

    <!-- Дополнительная информация -->
    <div v-if="showAdditionalInfo" class="mt-12 text-center text-sm text-gray-500 dark:text-gray-400">
      <p>
        Нажмите на кнопку выше, чтобы перейти в магазин приложений
        <br>
        и установить приложение на ваше устройство
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
// Пропсы
const props = defineProps({
  androidUrl: {
    type: String,
    default: null,
  },
  iosUrl: {
    type: String,
    default: null,
  },
  title: {
    type: String,
    default: 'Скачайте наше приложение',
  },
  subtitle: {
    type: String,
    default: 'Доступно на Android и iOS',
  },
  deviceType: {
    type: String,
    default: null,
  },
  showDeviceInfo: {
    type: Boolean,
    default: false, // Включить в dev режиме для отладки
  },
  showAdditionalInfo: {
    type: Boolean,
    default: true,
  },
})

// События
const emit = defineEmits<{
  androidClick: []
  iosClick: []
}>()

// Обработчики кликов.
//
// Переход больше не делаем сами: кнопки стали ссылками, и window.open поверх
// href открыл бы магазин двумя вкладками. Здесь остаётся только событие —
// страница вешает на него свою аналитику.
const handleAndroidClick = () => {
  if (props.androidUrl) emit('androidClick')
}

const handleIosClick = () => {
  if (props.iosUrl) emit('iosClick')
}
</script>

<style scoped lang="scss">
.app-download-buttons {
  @apply max-w-2xl mx-auto px-4 py-8;
}

.buttons-container {
  @apply flex flex-col gap-4 items-center;
}

// Элемент — <a>, поэтому block и no-underline обязательны: инлайновая ссылка
// проигнорировала бы ширину и вертикальные отступы.
.download-button {
  @apply block no-underline
         w-full max-w-md px-6 py-4 rounded-2xl
         transition-all duration-300 ease-in-out
         transform hover:scale-105 active:scale-95
         shadow-lg hover:shadow-xl;

  &.android-button {
    @apply bg-gradient-to-r from-green-500 to-green-600
           hover:from-green-600 hover:to-green-700
           text-white;
  }

  &.ios-button {
    @apply bg-gradient-to-r from-blue-500 to-blue-600
           hover:from-blue-600 hover:to-blue-700
           text-white;
  }
}

.button-content {
  @apply flex items-center gap-4;
}

.button-icon {
  @apply flex-shrink-0;
}

.button-text {
  @apply flex flex-col items-start text-left;
}

.button-label {
  @apply text-sm opacity-90;
}

.button-store {
  @apply text-lg font-semibold;
}

// Анимация появления кнопок
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.download-button {
  animation: fadeInUp 0.5s ease-out forwards;

  &:nth-child(1) {
    animation-delay: 0.1s;
  }

  &:nth-child(2) {
    animation-delay: 0.2s;
  }
}

// Темная тема
.dark {
  .download-button {
    @apply shadow-2xl;
  }
}
</style>
